import React, { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { HiOutlineChatBubbleLeftRight } from "react-icons/hi2";
import { supportApi } from "../../api/support";
import {
  APP_ROUTES,
  ISupportConversation,
  SupportConversationStatus,
} from "../../data-access";
import { refreshSupportUnread, useUser } from "../../hooks";
import { notify, playNotificationSound } from "../../util";
import { ContextCard } from "./context-card";
import { ConversationList } from "./conversation-list";
import { ConversationThread } from "./conversation-thread";
import "./support.scss";

const POLL_INTERVAL_MS = 10000;
const SEARCH_DEBOUNCE_MS = 350;

const baseClass = "support";

/**
 * Support inbox: the page the team answers users from, replacing the Intercom
 * agent interface. Three panes - conversation list, thread, user context.
 *
 * The route is kept out of the app for non-staff users as a convenience, not
 * as protection: every /api/support route is gated by the staff middleware
 * server-side, so a user who flips the flag in devtools gets 404s and an empty
 * page rather than anyone else's conversations.
 */
export const Support = (): JSX.Element => {
  const { currentUser } = useUser();
  const isStaff = currentUser?.is_staff === true;

  const [status, setStatus] = useState<SupportConversationStatus>("open");
  const [searchInput, setSearchInput] = useState<string>("");
  const [search, setSearch] = useState<string>("");
  // Strip the deep-link parameter once it has been consumed, so the agent's
  // later navigation does not keep dragging them back to the emailed thread.
  useEffect(() => {
    if (!searchParams.has("conversation")) return;
    const next = new URLSearchParams(searchParams);
    next.delete("conversation");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [conversations, setConversations] = useState<ISupportConversation[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  // Notification emails deep-link to a thread as /support?conversation={id},
  // so an agent lands on the message the email was about instead of hunting
  // for it. Read once on mount: after that the agent's own clicks own the
  // selection, and re-reading would drag them back to the emailed thread.
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedId, setSelectedId] = useState<string | null>(
    () => searchParams.get("conversation")
  );
  // The selected row as it was last seen in the list. The list is
  // last_message_at DESC and capped at one page, so a quiet conversation drops
  // off it while the agent is still reading and replying to that thread. The
  // pin keeps the thread mounted, and with it the half-written reply held in
  // its local state.
  const [pinnedConversation, setPinnedConversation] =
    useState<ISupportConversation | null>(null);

  // Manual loads and the 10s poll count separately. Sharing one counter meant a
  // poll could bump it past an in-flight manual load, whose finally then never
  // matched and never lowered isLoading - and the list gates its rows behind
  // that flag, so the pane spun forever.
  const manualRequestRef = useRef<number>(0);
  const silentRequestRef = useRef<number>(0);
  const manualInFlightRef = useRef<number>(0);
  // Sum of staff_unread across the list on the previous poll. A rise means a new
  // inbound message arrived - chime. null until the first load sets the baseline.
  const prevStaffUnreadRef = useRef<number | null>(null);

  useEffect(() => {
    const timeoutId = setTimeout(() => setSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  const loadConversations = useCallback(
    (isSilent: boolean): void => {
      const sequenceRef = isSilent ? silentRequestRef : manualRequestRef;

      sequenceRef.current += 1;
      const requestId = sequenceRef.current;

      if (!isSilent) {
        manualInFlightRef.current += 1;
        setIsLoading(true);
      }

      supportApi
        .getConversations({ status, search })
        .then((response) => {
          if (requestId !== sequenceRef.current) {
            return;
          }

          // A poll started before the agent changed a filter must not paint
          // the old filter's rows over the manual load that is still running.
          if (isSilent && manualInFlightRef.current > 0) {
            return;
          }

          // Chime when a poll shows more unread than before (a new inbound
          // message). Only on a silent poll - a manual load is a filter switch,
          // whose different total must not be mistaken for a new message.
          const totalStaffUnread = response.data.reduce(
            (sum, item) => sum + (item.staff_unread_count || 0),
            0
          );

          if (
            isSilent &&
            prevStaffUnreadRef.current !== null &&
            totalStaffUnread > prevStaffUnreadRef.current
          ) {
            playNotificationSound();
          }
          prevStaffUnreadRef.current = totalStaffUnread;

          setConversations(response.data);
          setTotal(response.meta.total);

          // Only a manual load picks a thread; a poll never moves the
          // selection out from under whoever is reading.
          if (!isSilent) {
            setSelectedId((current) =>
              current && response.data.some((item) => item.id === current)
                ? current
                : response.data[0]?.id ?? null
            );
          }
        })
        .catch((error: { text?: string }) => {
          if (requestId !== sequenceRef.current || isSilent) {
            return;
          }

          notify("error", error?.text || "Failed to load conversations");
        })
        .finally(() => {
          if (isSilent) {
            return;
          }

          manualInFlightRef.current = Math.max(
            manualInFlightRef.current - 1,
            0
          );

          // Only a manual load raises the flag, and the newest manual load
          // always reaches this point, so the spinner always comes back down.
          if (requestId === manualRequestRef.current) {
            setIsLoading(false);
          }
        });
    },
    [status, search]
  );

  useEffect(() => {
    if (!isStaff) {
      return;
    }

    loadConversations(false);

    const intervalId = setInterval(() => loadConversations(true), POLL_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [isStaff, loadConversations]);

  const handleStatusFilter = (nextStatus: SupportConversationStatus): void => {
    if (nextStatus === status) {
      return;
    }

    setSelectedId(null);
    setPinnedConversation(null);
    setStatus(nextStatus);
  };

  // Fired once the thread's read call has come back 204, so the sidebar badge
  // drops with the row instead of hanging on until the next 30s tick.
  const handleRead = useCallback((conversationId: string): void => {
    setConversations((current) =>
      current.map((item) =>
        item.id === conversationId ? { ...item, staff_unread_count: 0 } : item
      )
    );
    refreshSupportUnread();
  }, []);

  const handleStatusChange = useCallback(
    (updated: ISupportConversation): void => {
      // The thread just moved to the other filter, so it leaves this list.
      setConversations((current) => current.filter((item) => item.id !== updated.id));
      setSelectedId((selected) => (selected === updated.id ? null : selected));
      setPinnedConversation((pinned) =>
        pinned?.id === updated.id ? null : pinned
      );
      setTotal((current) => Math.max(current - 1, 0));
    },
    []
  );

  const handleReplySent = useCallback((): void => {
    loadConversations(true);
  }, [loadConversations]);

  const selectedFromList =
    conversations.find((item) => item.id === selectedId) ?? null;

  // Remember the row while it is still on the page, so the thread survives the
  // moment it falls off it.
  useEffect(() => {
    if (selectedFromList) {
      setPinnedConversation(selectedFromList);
    }
  }, [selectedFromList]);

  if (!isStaff) {
    return <Navigate to={APP_ROUTES.dashboard} replace />;
  }

  // Falls back to the pinned copy. The id is what keeps ConversationThread
  // mounted, and the thread holds the reply the agent is typing.
  const selectedConversation =
    selectedFromList ??
    (pinnedConversation && pinnedConversation.id === selectedId
      ? pinnedConversation
      : null);

  return (
    <div className={baseClass}>
      <div className={`${baseClass}__heading`}>
        <h1>Support</h1>
        <span>Conversations from the in-app chat widget</span>
      </div>

      <div className={`${baseClass}__panes`}>
        <div className={`${baseClass}__pane ${baseClass}__pane--list`}>
          <ConversationList
            conversations={conversations}
            selectedId={selectedId}
            status={status}
            searchValue={searchInput}
            total={total}
            isLoading={isLoading}
            onSelect={setSelectedId}
            onStatusChange={handleStatusFilter}
            onSearchChange={setSearchInput}
          />
        </div>

        <div className={`${baseClass}__pane ${baseClass}__pane--thread`}>
          {selectedConversation ? (
            <ConversationThread
              key={selectedConversation.id}
              conversation={selectedConversation}
              onRead={handleRead}
              onStatusChange={handleStatusChange}
              onReplySent={handleReplySent}
            />
          ) : (
            <div className={`${baseClass}__placeholder`}>
              <HiOutlineChatBubbleLeftRight size={32} />
              <span>Select a conversation to read it</span>
            </div>
          )}
        </div>

        <div className={`${baseClass}__pane ${baseClass}__pane--context`}>
          {selectedConversation ? (
            <ContextCard conversationId={selectedConversation.id} />
          ) : (
            <div className={`${baseClass}__placeholder`}>
              <span>No conversation selected</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Support;
